/*
 * DD AdminUI
 * Copyright (C) Fraunhofer IESE 2019 - 2025 Stefan Schweitzer, Shyalan Ramesh, Rafael Aranda López King,
 * Florian Wege, Johannes Schneider, Kazi Rezoanur Rahman, Christoph Müller, Pedram Jokar,
 * Mher Ter-Tovmasyan, Ben Burkhard, Matthias Gerbershagen, Anna Kleiner, Muhammad Tariq,
 * Adeline Silva Schäfer
 *
 * SPDX-License-Identifier: Apache-2.0
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { SetState } from 'zustand';
import { AppVariant, Filter, Filters, FilterType, Tenant, ValueOf } from '../../types';

export interface FilterSlice {
	filters: Filters;
	addFilter: (newFilter: Filter<AppVariant | Tenant>) => void;
	addFilters: (filterType: ValueOf<typeof FilterType>, filterItems: Filter<AppVariant | Tenant>[]) => void;
	removeFilter: (removeFilter: Filter<AppVariant | Tenant>) => void;
	removeFilters: (filterType: ValueOf<typeof FilterType>, filterItems: Filter<AppVariant | Tenant>[]) => void;
	removeAllFilters: () => void;
}

const getInitialFiltersState = (): Filters => ({ [FilterType.APP_VARIANT]: {}, [FilterType.TENANT]: {} });

const createFilterSlice = (set: SetState<FilterSlice>): FilterSlice => ({
	filters: getInitialFiltersState(),
	addFilter: (newFilter): void =>
		set(state => {
			state.filters[newFilter.type][newFilter.id] = newFilter;
		}),
	addFilters: (filterType, filterItems): void =>
		set(state => {
			filterItems.forEach(filterItem => (state.filters[filterType][filterItem.id] = filterItem));
		}),
	removeFilters: (filterType, filterItems): void =>
		set(state => {
			filterItems.forEach(filterItem => delete state.filters[filterType][filterItem.id]);
		}),
	removeFilter: ({ id, type }): void =>
		set(state => {
			delete state.filters?.[type]?.[id];
		}),
	removeAllFilters: (): void =>
		set(state => {
			state.filters = getInitialFiltersState();
		})
});

export default createFilterSlice;
